"use client";

/**
 * Auth Context - Day 4: Auth Integration
 *
 * Provides authentication state and methods to the app.
 * Uses Supabase Auth for Google Sign-In.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Session, User } from "@supabase/supabase-js";
import { ANALYTICS_EVENTS, PERSON_PROPERTIES } from "@bash/shared";
import { supabase } from "@/lib/supabase/client";
import { posthog } from "@/lib/posthog";
import { isLoginTransition } from "@/lib/authTransition";
import { nativeInvoke, signInWithGoogleNative } from "@/lib/native-login";
import {
  pushVoiceSessionToNative,
  watchNativeSessionStale,
} from "@/lib/voice/nativeBridge";
import { usePurchaseStore } from "@/stores/purchaseStore";
import {
  clearUserScopedStorage,
  reconcileStorageOwner,
} from "@/lib/session-storage-guard";
import { lowerGateCurtain } from "@/lib/gate-curtain";
import { traceFlash } from "@/lib/flash-trace";
import { isDesktopShell } from "@/lib/desktop-shell";
import { keepDesktopSessionAlive } from "@/lib/auth/desktop-session-keepalive";
import { SESSION_HINT_COOKIE } from "@/lib/supabase/auth-cookies";
import { onSessionUnreachable } from "@/lib/supabase/server-held-session";

// =============================================================================
// Types
// =============================================================================

interface AuthContextValue {
  /** Current user (null if not logged in) */
  user: User | null;
  /** Current session (null if not logged in) */
  session: Session | null;
  /** Whether auth state is still loading */
  loading: boolean;
  /** Fast synchronous hint that a session exists (the session cookie itself is HttpOnly) */
  hasAuthCookie: boolean;
  /** The page has gone a while without learning its session from our origin (still retrying) */
  sessionUnreachable: boolean;
  /** Sign in with Google. Pass `next` to redirect after auth (e.g. '/routines'). */
  signInWithGoogle: (next?: string) => Promise<void>;
  /** Complete a Google One Tap sign-in: exchange the GIS ID token for a Supabase session. */
  signInWithGoogleIdToken: (token: string, nonce: string) => Promise<void>;
  /** Sign out */
  signOut: () => Promise<void>;
  /** Get current access token for socket auth */
  getAccessToken: () => Promise<string | null>;
}

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Tie this browser's PostHog identity to the signed-in user, under the SAME id
 * the server uses for user:signed_up (the Supabase user id). Lives here, not
 * in the app shell, because a new account lands on /onboarding — outside the
 * shell — and many never reach it; identify there never ran, so their web
 * events stayed anonymous and could not be joined to the sign-up. Every
 * sign-in path (OAuth redirect, One Tap, desktop, a restored session) ends in
 * the session read below. The façade queues it until posthog-js loads, and
 * posthog-js skips a repeat identify for the same user.
 */
function identifyForAnalytics(user: User): void {
  posthog.identify(user.id, {
    [PERSON_PROPERTIES.EMAIL]: user.email,
    [PERSON_PROPERTIES.NAME]:
      user.user_metadata?.full_name || user.user_metadata?.name,
  });
}

// =============================================================================
// Provider
// =============================================================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionUnreachable, setSessionUnreachable] = useState(false);
  // Read by signOut, which is created once.
  const sessionUnreachableRef = useRef(false);
  // Last user id seen by onAuthStateChange — distinguishes a genuine login
  // (null→user on SIGNED_IN) from token refreshes and INITIAL_SESSION restores
  // for the auth:signed_in analytics event.
  const prevAuthUserIdRef = useRef<string | null>(null);

  // Initialize auth state via onAuthStateChange (primary) + getSession() (eager fallback).
  //
  // onAuthStateChange fires INITIAL_SESSION once the Supabase client resolves the
  // session from cookies. If the JWT expired, this requires a network round trip to
  // refresh — which can take seconds on slow connections. During that window, AuthGuard
  // would redirect the user to the landing page (false logout).
  //
  // getSession() is also called eagerly. If it returns a valid session before
  // onAuthStateChange fires, we use it immediately. If it returns null, we do NOT
  // set loading=false — we wait for onAuthStateChange (which may succeed after refresh).
  // This avoids the old race condition where getSession(null) + setLoading(false)
  // caused premature redirect.
  //
  // ⚠️ It is NOT a safety net, despite reading like one: getSession() opens with
  // `await initializePromise` and so does onAuthStateChange before it emits
  // INITIAL_SESSION. Both block on the SAME promise, so this call cannot resolve
  // one millisecond before the listener it appears to back up. When that promise
  // hung on a signal-less token refresh, `loading` stayed true forever and the
  // gate curtain never lifted (founder, 08-04). The real guarantees live
  // elsewhere: lib/supabase/bounded-auth-fetch.ts puts a deadline on the refresh
  // so the promise always settles, and GateCurtain ships a CSS-only escape that
  // holds even when no JS runs at all. Do not add a third latch here.
  useEffect(() => {
    let resolved = false;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      resolved = true;
      // Any verdict means the session arrived: lift the trouble screen.
      sessionUnreachableRef.current = false;
      setSessionUnreachable(false);
      // Every event hands down a FRESH session object, which re-runs the auth
      // gate and anything else keyed on it — a token refresh can therefore
      // repaint the screen with nobody having asked. Trace it (§flash-trace).
      traceFlash("auth_event", event);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Identify first, so auth:signed_in below already carries the user id.
      if (session?.user) identifyForAnalytics(session.user);

      // auth:signed_in — a real login only: the SIGNED_IN event AND the
      // null→user transition. INITIAL_SESSION restores and token refreshes
      // never fire it; the façade queues it if PostHog is still initializing.
      const nextUserId = session?.user?.id ?? null;
      if (
        event === "SIGNED_IN" &&
        isLoginTransition(prevAuthUserIdRef.current, nextUserId)
      ) {
        posthog.capture(ANALYTICS_EVENTS.AUTH_SIGNED_IN, {
          provider: session?.user?.app_metadata?.provider ?? "unknown",
        });
      }
      prevAuthUserIdRef.current = nextUserId;

      // Privacy boundary: if a DIFFERENT user owns this browser's on-disk state
      // (a silent account switch with no explicit sign-out), wipe it and reload
      // so no per-user store hydrates the previous account's data. No-op for the
      // same user (normal load / token refresh) and for a fresh/clean browser.
      if (session?.user) {
        reconcileStorageOwner(session.user.id);
      }

      // Desktop only: hand the session to the native shell so Rust mints the Deepgram
      // token itself (overlay WebView off the voice hot path). Fires on sign-in AND on
      // token refresh, so Rust always has a fresh token. No-op in a normal browser.
      pushVoiceSessionToNative(session);

      // Hard sign-out: session refresh failed or user signed out elsewhere.
      // Bounce to /login ONLY from authenticated app routes, so the app never
      // sits half-authed with a stale access_token that 401s on every request.
      // Public surfaces (marketplace /skills,/agents,/abilities; marketing /,
      // /pricing; /blogs; /policies; /v; /login; /auth) render fine for anon and
      // must NOT be login-walled. Allowlist the protected (app) prefixes rather
      // than denylist public ones — public routes are added often and a stale
      // denylist silently login-walls them (this regression's root cause).
      if (event === "SIGNED_OUT" && typeof window !== "undefined") {
        const path = window.location.pathname;
        const PROTECTED_PREFIXES = [
          "/home",
          "/task",
          "/account",
          "/routines",
          "/knowledge",
          "/memory",
          "/vault",
          "/apps",
          "/inbox",
          "/support",
          "/prep",
          "/email",
          "/agents/new",
          // The shell's Dock window (spec 2026-09-22 §4: signed out, the Dock
          // window hides). The edge gate walls it too, but that only runs on a
          // document request: a sign-out while the panel is already open never
          // asks the edge anything, and a live Dock over a dead session is
          // exactly the half-authed state this bounce exists to prevent.
          "/dock",
          // The developer console (Platform API v1, spec 3.17): the same
          // reasoning as the Dock. A company's keys and users on screen must
          // not outlive a sign-out in another tab. /developers (docs) stays open.
          "/developers/console",
        ];
        const isProtected = PROTECTED_PREFIXES.some(
          (p) => path === p || path.startsWith(p + "/")
        );
        if (isProtected) {
          window.location.replace(`/login?next=${encodeURIComponent(path)}`);
        }
      }
    });

    // Eager fallback: only act on successful session (never on null)
    supabase.auth
      .getSession()
      .then(({ data: { session: eagarSession } }) => {
        if (!resolved && eagarSession) {
          setSession(eagarSession);
          setUser(eagarSession.user);
          setLoading(false);
          identifyForAnalytics(eagarSession.user);
          pushVoiceSessionToNative(eagarSession);
          // Same privacy boundary as the onAuthStateChange path, for the case
          // where this eager read renders the user before that listener fires.
          reconcileStorageOwner(eagarSession.user.id);
        }
      })
      .catch(() => {
        // Ignore — onAuthStateChange is the authoritative source
      });

    // Desktop self-heal: when the shell's cached session dies (refresh-token
    // rotation race), it emits a stale nudge; re-push our CURRENT session —
    // this client is the single legitimate rotator. No-op in a browser.
    const unwatchStale = watchNativeSessionStale(() => {
      void supabase.auth.getSession().then(({ data }) => {
        if (data.session) pushVoiceSessionToNative(data.session);
      });
    });

    // Desktop shell: keep the token refreshing even while the window is hidden.
    // Supabase's browser default stops auto-refresh on `document.hidden` and only
    // resumes when visible — correct for a tab, fatal for an app that lives
    // behind other windows. 08-04: hidden for 8h, no refresh fired, the token
    // died at 04:48, and the next message streamed onto a socket the server
    // refused as expired, leaving the composer on "Thinking" with no way out.
    // Nobody relaunches an app after being away; the session has to survive.
    const stopKeepalive = isDesktopShell()
      ? keepDesktopSessionAlive(supabase.auth, {
          addEventListener: (t, l) => document.addEventListener(t, l),
          removeEventListener: (t, l) => document.removeEventListener(t, l),
          isHidden: () => document.hidden,
        })
      : () => {};

    // The session could not be fetched from our origin for a while: say so
    // (AuthGuard's trouble screen) instead of holding the curtain in silence.
    // The fetch keeps retrying; the first auth event above lifts it again.
    const stopUnreachable = onSessionUnreachable(() => {
      if (resolved) return;
      sessionUnreachableRef.current = true;
      setSessionUnreachable(true);
    });

    return () => {
      subscription.unsubscribe();
      unwatchStale();
      stopKeepalive();
      stopUnreachable();
    };
  }, []);

  // Sign in with Google. Both legs start at /auth/google on the server, which
  // writes the PKCE verifier as an HttpOnly cookie (CASA 2.3.2): page memory
  // would not survive the redirect to Google and back.
  const signInWithGoogle = useCallback(async (next?: string) => {
    // Desktop shell: the embedded webview can't show passkey/Touch ID prompts,
    // so the OAuth leg runs in the shell's Safari-backed sheet and the PKCE
    // code finishes back here through /auth/callback (see lib/native-login).
    const invoke = nativeInvoke(window);
    if (invoke) {
      await signInWithGoogleNative(window.location.origin, invoke);
      return;
    }

    const start = new URL("/auth/google", window.location.origin);
    if (next) {
      start.searchParams.set("next", next);
    }
    window.location.assign(start.toString());
  }, []);

  // Google One Tap: GIS hands us a signed ID token in-page; exchange it for a
  // Supabase session directly — no OAuth redirect. `nonce` is the RAW value whose
  // SHA-256 hash was given to GIS (see lib/google-one-tap). The exchange itself
  // runs on our server (/auth/token), so the new session lands in the HttpOnly
  // cookie; the page gets it through onAuthStateChange like any other sign-in.
  const signInWithGoogleIdToken = useCallback(
    async (token: string, nonce: string) => {
      const { error } = await supabase.auth.signInWithIdToken({
        provider: "google",
        token,
        nonce,
      });
      if (error) {
        console.error("Google One Tap sign-in error:", error);
        throw error;
      }
    },
    []
  );

  // Sign out
  const signOut = useCallback(async () => {
    // Analytics leaves with the user: capture while the identity is still
    // attached, then reset the PostHog identity HERE — this is the seam every
    // sign-out path funnels through (app shell menu, beta-gate screen,
    // dead-session bounce, account deletion), so the next user of this
    // browser can never inherit the previous distinct_id.
    posthog.capture(ANALYTICS_EVENTS.AUTH_SIGNED_OUT);
    posthog.reset();
    // FIRST, before anything async: drop the gate curtain. The chrome must
    // never show during session teardown ("clicking signout shows home for a
    // bit" — founder, 08-03), and the next cold load on this browser must
    // start covered again.
    lowerGateCurtain();
    // Wipe per-user client state BEFORE the session goes away, so the next
    // account on this browser can never hydrate this user's tasks/messages.
    // This is the seam every sign-out path funnels through (app shell menu,
    // beta-gate screen, dead-session bounce), so one call covers them all.
    clearUserScopedStorage();
    // ...and the in-memory pay step and budgets (agent wallet): no next person sees them.
    usePurchaseStore.getState().reset();
    // Session never arrived (our origin unreachable): supabase-js is still
    // waiting for it and would hold this sign-out behind that wait. /auth/reset
    // destroys the cookie server-side with no JS session needed.
    if (sessionUnreachableRef.current) {
      window.location.assign("/auth/reset");
      return;
    }
    // The server-side sign-out FAILS for a session whose user no longer
    // exists (account just deleted in-product — the founder's delete-and-
    // rejoin loop). supabase-js does not clear the local session on a server
    // error, and throwing here left the browser wedged on a dead session
    // (blank /home, logout button dead — live 2026-08-02). The contract of
    // this function is "this browser is signed out": always fall back to a
    // local-scope sign-out, and never throw.
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Sign out error (falling back to local):", error);
        await supabase.auth.signOut({ scope: "local" });
      }
    } catch (err) {
      console.error("Sign out threw (falling back to local):", err);
      await supabase.auth.signOut({ scope: "local" }).catch(() => {});
    }
  }, []);

  // Get access token for socket auth
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  }, []);

  // The session cookie is HttpOnly, invisible here; the server keeps a
  // secret-free hint beside it for this synchronous first-paint check.
  const hasAuthCookie =
    typeof document !== "undefined" &&
    document.cookie.includes(`${SESSION_HINT_COOKIE}=1`);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      loading,
      hasAuthCookie,
      sessionUnreachable,
      signInWithGoogle,
      signInWithGoogleIdToken,
      signOut,
      getAccessToken,
    }),
    [
      user,
      session,
      loading,
      hasAuthCookie,
      sessionUnreachable,
      signInWithGoogle,
      signInWithGoogleIdToken,
      signOut,
      getAccessToken,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
